import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useEducation } from "../../hooks/useEducation";
import {
  ArrowBack,
  AccessTime,
  School,
  Person,
  Category,
  Check,
  LocalOffer,
} from "@mui/icons-material";
import "./CourseDetail.css";
import { useToast } from "../../components/Toast/ToastContext";
import CourseDetailDesc from "./CourseDetailDesc";
import "./index.css";
import ClassCard from "./MyCourses/ClassCard";
import CourseCard from "./CourseCard";

const CourseDetail = () => {
  const { showToast } = useToast();
  const { courseId } = useParams();
  const navigate = useNavigate();
  const {
    myCourses,
    currentCourse,
    currentCourseLoading,
    currentCourseError,
    fetchCourse,
    enrollInCourse,
    fetchMyCourses,
    getClassesByCourse,
    getContentsByClass,
    toggleFavoriteCourse,
  } = useEducation();

  const [enrollmentStatus, setEnrollmentStatus] = useState({
    isEnrolled: false,
    enrollmentId: null,
    isLoading: false,
    error: null,
  });

  const [contentsByClass, setContentsByClass] = useState({});
  const [contentsLoading, setContentsLoading] = useState(false);

  // Fetch contents for all classes
  const fetchAllContents = async (classes) => {
    setContentsLoading(true);
    const contentsMap = {};

    try {
      for (const classItem of classes) {
        const contents = await getContentsByClass(classItem.id);
        contentsMap[classItem.id] = contents;
      }
      setContentsByClass(contentsMap);
    } catch (error) {
      console.error("Failed to fetch contents:", error);
    } finally {
      setContentsLoading(false);
    }
  };

  // Auto-enroll and fetch course details
  useEffect(() => {
    const loadCourseDataAndEnroll = async () => {
      try {
        // Fetch course details
        await fetchCourse(courseId);

        // Fetch classes for this course
        const classes = await getClassesByCourse(courseId);

        // Fetch contents for all classes
        await fetchAllContents(classes);

        // Get user's enrolled courses
        const myCoursesData = await fetchMyCourses();

        // Check if user is already enrolled
        const enrolledCourse = myCoursesData.find(
          (course) => course.course === courseId
        );

        if (enrolledCourse) {
          // Already enrolled
          setEnrollmentStatus({
            isEnrolled: true,
            enrollmentId: enrolledCourse.id,
            isLoading: false,
            error: null,
          });
        } else {
          // Auto-enroll the user
          setEnrollmentStatus((prev) => ({ ...prev, isLoading: true }));

          try {
            const enrollmentData = await enrollInCourse(courseId);
            setEnrollmentStatus({
              isEnrolled: true,
              enrollmentId: enrollmentData.id,
              isLoading: false,
              error: null,
            });

            // Show success message
            showToast("Successfully enrolled in the course!", "success");
          } catch (enrollError) {
            setEnrollmentStatus({
              isEnrolled: false,
              enrollmentId: null,
              isLoading: false,
              error: enrollError.message || "Failed to enroll in the course",
            });
          }
        }
      } catch (error) {
        console.error("Error loading course data:", error);
        setEnrollmentStatus((prev) => ({
          ...prev,
          isLoading: false,
          error: "Failed to load course data",
        }));
      }
    };

    loadCourseDataAndEnroll();
  }, [
    courseId,
    fetchCourse,
    fetchMyCourses,
    enrollInCourse,
    showToast,
    getClassesByCourse,
    getContentsByClass,
    toggleFavoriteCourse,
  ]);

  const goBack = () => {
    navigate(-1);
  };

  // Handle favorite toggle
  const handleFavoriteToggle = async (e, courseId) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      // Find the enrolled course
      const enrolledCourse = myCourses.find(
        (course) => course.course === courseId
      );

      if (enrolledCourse) {
        await toggleFavoriteCourse(enrolledCourse.id);
        // Refresh my courses list to get updated favorite status
        await fetchMyCourses();
        showToast(
          enrolledCourse.is_favorite
            ? "Removed from favorites"
            : "Added to favorites",
          "success"
        );
      }
    } catch (error) {
      console.error("Failed to toggle favorite:", error);
      showToast("Failed to update favorite status", "error");
    }
  };

  if (currentCourseLoading) {
    return (
      <div className="course-detail-loading">Loading course details...</div>
    );
  }

  if (currentCourseError) {
    return (
      <div className="course-detail-error">
        <p>Error loading course: {currentCourseError}</p>
        <button onClick={goBack} className="course-btn">
          Go Back
        </button>
      </div>
    );
  }

  if (!currentCourse) {
    return (
      <div className="course-detail-not-found">
        <p>Course not found</p>
        <button onClick={goBack} className="course-btn">
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="course-detail-container education-container">
      <div className="course-detail-header">
        <button onClick={goBack} className="course-back-btn">
          <ArrowBack /> Back to Courses
        </button>
        <div style={{ display: "flex" }}>
          <CourseCard
            course={currentCourse}
            myCourses={myCourses}
            onFavoriteToggle={handleFavoriteToggle}
          />

          <CourseDetailDesc
            name={currentCourse.name}
            desc={currentCourse.description}
          />
        </div>
      </div>

      <div className="course-detail-content">
        <div className="course-detail-main">
          <div className="course-detail-info">
            <div className="course-detail-meta">
              <div className="course-meta-item">
                <AccessTime />
                <span>Duration: {currentCourse.duration || "N/A"} min</span>
              </div>

              <div className="course-meta-item">
                <School />
                <span>Class Type: {currentCourse.class_type || "N/A"}</span>
              </div>

              {currentCourse.instructor && (
                <div className="course-meta-item">
                  <Person />
                  <span>Instructor: {currentCourse.instructor}</span>
                </div>
              )}
              {currentCourse.category && (
                <div className="course-detail-category">
                  <Category />
                  <span>{currentCourse.category_name}</span>
                </div>
              )}

              {currentCourse.class_count && (
                <div className="course-meta-item">
                  <Check />
                  <span>{currentCourse.class_count} Classes</span>
                </div>
              )}
            </div>
            {currentCourse.keywords && currentCourse.keywords.length > 0 && (
              <div className="course-detail-keywords">
                <h3>Keywords</h3>
                <div className="keyword-tags">
                  {currentCourse.keywords.map((keyword) => (
                    <span key={keyword.id} className="keyword-tag">
                      <LocalOffer fontSize="small" />
                      {keyword.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {currentCourse.classes && currentCourse.classes.length > 0 && (
          <div className="course-content-container">
            {/* Contents sidebar */}
            <div className="course-contents-sidebar">
              <h3>Moduler</h3>
              {contentsLoading ? (
                <div>Loading contents...</div>
              ) : (
                <div className="contents-list">
                  {currentCourse.classes.map((classItem) => (
                    <div key={classItem.id} className="class-contents">
                      <h4 className="class-title">{classItem.name}</h4>
                      {contentsByClass[classItem.id]?.length > 0 ? (
                        <ul className="contents-items">
                          {contentsByClass[classItem.id].map((content) => (
                            <li key={content.id} className="content-item">
                              {content.title}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="no-contents">No contents available</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Classes list */}
            <div className="course-detail-classes">
              <div className="class-list">
                {currentCourse.classes.map((classItem, index) => (
                  <ClassCard
                    key={classItem.id}
                    classItem={classItem}
                    courseId={courseId}
                    enrollmentId={enrollmentStatus.enrollmentId}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseDetail;
